import React, { useMemo } from 'react';
import { OrganizerProfile } from '../../../types/organizer.types';
import { Icon } from '../../../pages/DesignSystem';
import './ProfileLivePreview.css';

interface ProfileLivePreviewProps {
  formData: Partial<OrganizerProfile>;
}

/**
 * ProfileLivePreview - Real-time preview of profile as it appears to public
 * Updates in real-time as user edits the form
 */
const ProfileLivePreview: React.FC<ProfileLivePreviewProps> = ({ formData }) => {
  // Build preview profile from form data
  const previewProfile = useMemo(() => ({
    organizer: formData.organizer || 'Your Organization Name',
    subtitle: formData.subtitle || '',
    description: formData.description || '',
    organizerImage: formData.organizerImage || '',
    galleryImages: formData.galleryImages || [],
    location: formData.location || '',
    locationName: formData.locationName || 'Your Location',
    email: formData.email || formData.organizerContact || '',
    phone: formData.phone || '',
    website: formData.website || '',
    instagram: formData.instagram || ''
  }), [formData]);

  const hasAvatar = !!previewProfile.organizerImage;
  const hasGallery = previewProfile.galleryImages.length > 0;
  const hasContact = !!(previewProfile.email || previewProfile.phone || previewProfile.website || previewProfile.instagram);

  return (
    <div className="profile-live-preview">
      <div className="profile-live-preview__header">
        <Icon name="Eye" size={16} />
        <span>Public Profile Preview</span>
      </div>

      <div className="profile-live-preview__content">
        {/* Profile Card Preview */}
        <div className="profile-live-preview__card">
          {/* Gallery / Cover */}
          <div className="profile-live-preview__cover">
            {hasGallery ? (
              <img
                src={previewProfile.galleryImages[0]}
                alt="Cover"
                className="profile-live-preview__cover-image"
              />
            ) : (
              <div className="profile-live-preview__cover-placeholder">
                <Icon name="Images" size={24} />
                <span>Add gallery images</span>
              </div>
            )}
          </div>

          {/* Avatar & Info */}
          <div className="profile-live-preview__info">
            <div className="profile-live-preview__avatar-wrapper">
              {hasAvatar ? (
                <img
                  src={previewProfile.organizerImage}
                  alt={previewProfile.organizer}
                  className="profile-live-preview__avatar"
                />
              ) : (
                <div className="profile-live-preview__avatar profile-live-preview__avatar--placeholder">
                  <Icon name="User" size={24} />
                </div>
              )}
            </div>

            <div className="profile-live-preview__details">
              <h3 className="profile-live-preview__name">
                {previewProfile.organizer}
              </h3>
              {previewProfile.subtitle && (
                <p className="profile-live-preview__subtitle">
                  {previewProfile.subtitle}
                </p>
              )}
              <div className="profile-live-preview__location">
                <Icon name="MapPin" size={12} />
                <span>{previewProfile.locationName || previewProfile.location || 'Add location'}</span>
              </div>
            </div>
          </div>

          {/* Description */}
          {previewProfile.description && (
            <div className="profile-live-preview__description">
              <p>{previewProfile.description}</p>
            </div>
          )}

          {/* Contact Info */}
          {hasContact && (
            <div className="profile-live-preview__contact">
              {previewProfile.email && (
                <div className="profile-live-preview__contact-item">
                  <Icon name="Mail" size={14} />
                  <span>{previewProfile.email}</span>
                </div>
              )}
              {previewProfile.phone && (
                <div className="profile-live-preview__contact-item">
                  <Icon name="Phone" size={14} />
                  <span>{previewProfile.phone}</span>
                </div>
              )}
              {previewProfile.website && (
                <div className="profile-live-preview__contact-item">
                  <Icon name="Globe" size={14} />
                  <span>{previewProfile.website.replace(/^https?:\/\//, '')}</span>
                </div>
              )}
              {previewProfile.instagram && (
                <div className="profile-live-preview__contact-item">
                  <Icon name="Instagram" size={14} />
                  <span>@{previewProfile.instagram.replace(/^@/, '')}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Helper text */}
        <div className="profile-live-preview__helper">
          <Icon name="Info" size={14} />
          <span>This is how your profile appears to visitors</span>
        </div>
      </div>
    </div>
  );
};

export default ProfileLivePreview;
